import { useEffect, useState } from 'react';
import { Panel, PanelType, Stack } from '@fluentui/react';
import {
  Button,
  Dropdown,
  Field,
  FluentProvider,
  Input,
  Option,
  webLightTheme,
} from '@fluentui/react-components';
import { useSetAtom } from 'jotai';
import {
  errorMessageAtom,
  isInProgressAtom,
  successMessageAtom,
} from '../../atoms/messageBarAtoms';
import { IMetadata, updateMetadata } from '../../services/metadataService';

const ENTITY_TYPE_OPTIONS = [
  'Asset',
  'Contractor',
  'Group',
  'Issue',
  'Order',
  'Rent',
  'Renter',
  'User',
  'Work',
  'Zone',
];

interface ImportMetadataPanelProps {
  isOpen: boolean;
  onDismiss: () => void;
  onImported?: () => void;
}

const ImportMetadataPanel = ({
  isOpen,
  onDismiss,
  onImported,
}: ImportMetadataPanelProps) => {
  const setSuccessMessage = useSetAtom(successMessageAtom);
  const setErrorMessage = useSetAtom(errorMessageAtom);
  const setIsInProgress = useSetAtom(isInProgressAtom);

  const [entityType, setEntityType] = useState('');
  const [entityName, setEntityName] = useState('');
  const [file, setFile] = useState<File>();
  const [fileName, setFileName] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setEntityType('');
    setEntityName('');
    setFile(undefined);
    setFileName('');
  }, [isOpen]);

  const handleConfirm = async () => {
    const trimmedEntityType = entityType.trim();
    const trimmedName = entityName.trim();

    if (!trimmedEntityType) {
      setErrorMessage('Select an entity type before importing.');
      return;
    }

    if (!file) {
      setErrorMessage('Select a JSON file to import.');
      return;
    }

    let parsedMetadata: IMetadata;
    try {
      const raw = await file.text();
      parsedMetadata = JSON.parse(raw) as IMetadata;
    } catch (error: any) {
      setErrorMessage(
        error?.message
          ? `Invalid JSON file. ${error.message}`
          : 'Invalid JSON file.'
      );
      return;
    }

    const abortController = new AbortController();
    try {
      setIsInProgress(true);
      const response = await updateMetadata(
        abortController,
        trimmedEntityType,
        parsedMetadata,
        trimmedName || undefined
      );
      setSuccessMessage(`Version: ${response.version.toString()}`);
      onImported?.();
      onDismiss();
    } catch (error: any) {
      setErrorMessage(error.message);
    } finally {
      setIsInProgress(false);
    }
  };

  return (
    <Panel
      isOpen={isOpen}
      onDismiss={onDismiss}
      type={PanelType.smallFixedFar}
      closeButtonAriaLabel="Close"
      headerText="Import metadata"
    >
      <FluentProvider theme={webLightTheme}>
        <Stack tokens={{ childrenGap: 12 }}>
          <Field label="JSON file">
            <Stack tokens={{ childrenGap: 8 }}>
              <input
                type="file"
                accept=".json,application/json"
                onChange={(event) => {
                  const picked = event.target.files?.[0];
                  setFile(picked);
                  setFileName(picked?.name ?? '');
                }}
              />
              <Input
                value={fileName}
                placeholder="No file selected"
                readOnly={true}
              />
            </Stack>
          </Field>
          <Field label="Entity type">
            <Dropdown
              value={entityType}
              selectedOptions={entityType ? [entityType] : []}
              onOptionSelect={(_event, data) =>
                setEntityType(data.optionValue ?? '')
              }
            >
              {ENTITY_TYPE_OPTIONS.map((option) => (
                <Option key={option} value={option}>
                  {option}
                </Option>
              ))}
            </Dropdown>
          </Field>
          <Field label="Entity name">
            <Input
              value={entityName}
              onChange={(_event, data) => setEntityName(data.value)}
            />
          </Field>
          <Stack horizontal tokens={{ childrenGap: 8 }}>
            <Button appearance="primary" onClick={handleConfirm}>
              Import
            </Button>
            <Button appearance="secondary" onClick={onDismiss}>
              Cancel
            </Button>
          </Stack>
        </Stack>
      </FluentProvider>
    </Panel>
  );
};

export default ImportMetadataPanel;
